import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Onboarding() {
  const nav = useNavigate();
  const { refreshAccount } = useAuth();
  const [phone, setPhone] = useState("");
  const [conn, setConn] = useState(null);
  const [status, setStatus] = useState("idle");
  const pollRef = useRef(null);

  const connect = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/whatsapp/connect", { phone });
      setConn(data);
      setStatus("connecting");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  useEffect(() => {
    if (status !== "connecting") return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/whatsapp/status");
        if (data.status === "connected") {
          clearInterval(pollRef.current);
          setStatus("connected");
          await refreshAccount();
          toast.success("WhatsApp terhubung!");
          setTimeout(() => nav("/chats"), 1500);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [status, nav, refreshAccount]);

  return (
    <main className="hero-glow mx-auto flex min-h-screen w-full max-w-5xl items-center px-6 py-16 lg:px-10">
      <div className={`grid w-full items-center gap-14 ${conn ? "lg:grid-cols-2 lg:gap-0" : ""}`}>
        <div className={`w-full max-w-md ${conn ? "max-lg:hidden" : "mx-auto"}`}>
          <div className="mx-auto mb-6 flex w-fit items-center gap-1.5 rounded-2xl rounded-bl-md border border-mint/20 bg-mint/[0.05] px-5 py-3" role="img" aria-label="Bot sedang mengetik">
            {[0, 1, 2].map((i) => (
              <span key={i} className="h-2 w-2 animate-bounce-dot rounded-full bg-mint" style={{ animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
          <h1 className="font-display text-center text-4xl font-semibold leading-[1.1] tracking-[-0.03em] md:text-5xl">
            Hubungkan <span className="text-mint">WhatsApp</span> Anda
          </h1>
          <p className="mt-4 text-center text-muted">Nomor ini yang akan dijawab otomatis oleh ChatSensei.</p>
          <form onSubmit={connect} className="mt-9">
            <label htmlFor="whatsapp" className="text-sm font-medium">Nomor WhatsApp</label>
            <div className="group relative mt-3">
              <input id="whatsapp" data-testid="onboarding-phone-input" type="tel" inputMode="tel" required
                pattern="[0-9+]{8,16}" placeholder="6281234567890" autoComplete="tel"
                value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full border-b border-white/15 bg-transparent py-3 font-mono text-lg tracking-tight caret-mint placeholder:text-faint focus:outline-none" />
              <span className="pointer-events-none absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 bg-mint transition-transform duration-300 group-focus-within:scale-x-100" aria-hidden="true" />
            </div>
            <p className="mt-3 text-[12.5px] leading-relaxed text-faint">Sertakan kode negara. Ini nomor yang akan dihubungkan ChatSensei.</p>
            <button type="submit" data-testid="onboarding-connect-btn" disabled={!phone.trim()}
              className="mt-9 flex w-full items-center justify-center gap-2.5 rounded-full bg-mint py-4 text-[15px] font-semibold text-ink transition hover:-translate-y-px hover:opacity-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint disabled:pointer-events-none disabled:opacity-40">
              Lanjutkan
              <ArrowRight size={16} strokeWidth={2.4} />
            </button>
          </form>
        </div>

        {conn && (
          <div className="qr-enter max-w-md justify-self-center text-center lg:justify-self-end lg:border-l lg:border-white/5 lg:pl-20">
            <div className="relative mx-auto w-fit p-5">
              <span className="absolute left-0 top-0 h-7 w-7 border-l-2 border-t-2 border-mint/50" />
              <span className="absolute right-0 top-0 h-7 w-7 border-r-2 border-t-2 border-mint/50" />
              <span className="absolute bottom-0 left-0 h-7 w-7 border-b-2 border-l-2 border-mint/50" />
              <span className="absolute bottom-0 right-0 h-7 w-7 border-b-2 border-r-2 border-mint/50" />
              <div className="relative overflow-hidden bg-white p-3 shadow-2xl shadow-black/60">
                {status === "connecting" && <span className="scan-line" aria-hidden="true" />}
                <img src={conn.qr} alt="QR Code WhatsApp" width={240} height={240} className="h-56 w-56" data-testid="onboarding-qr-img" />
                {status === "connected" && (
                  <div className="absolute inset-0 flex items-center justify-center bg-ink/85">
                    <CheckCircle2 size={56} className="text-mint" />
                  </div>
                )}
              </div>
            </div>
            <p className="mt-6 text-sm text-muted">Scan dengan aplikasi WhatsApp untuk menghubungkan</p>
            <p data-testid="onboarding-connected-number" className="mt-1 font-mono text-lg text-mint">{phone}</p>
            {conn.pairing_code && <>
              <p className="mt-4 text-xs text-faint">atau masukkan pairing code:</p>
              <p data-testid="onboarding-pairing-code" className="mt-1 font-mono text-xl font-semibold tracking-widest text-mint">{conn.pairing_code}</p>
            </>}
            <p className="mt-5 font-mono text-xs text-faint">WhatsApp &gt; Settings &gt; Linked devices</p>
            {status === "connected" && <p className="mt-4 text-sm font-semibold text-mint">Terhubung! Mengalihkan ke dashboard…</p>}
          </div>
        )}
      </div>
    </main>
  );
}
